import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import {
  getStudentAttendanceGrid,
  saveStudentAttendance,
  processStudentDeviceLog,
} from "../../api/attendanceApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const academicYearApi = createLookupApi("academic-years");

const STATUS_LABELS = { 0: "Not taken", 1: "Present", 2: "Absent", 3: "Leave", 4: "Not mapped" };

function today() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Direct port of digicampusbd's `student-attendance-input`/`-update` manual
 * "take attendance" grid. Two real gaps found while sweeping the Attendance
 * module (see MIGRATION.md), both fixed here: the real page's own required
 * Academic Year selector was missing (the roster always came from the
 * institute's single "current" year, so a past year's class-configuration
 * roster could never be viewed or corrected here), and the real page's "Send
 * Sms" checkbox (guardian absent-notification on save — distinct from the
 * already-built automated-attendance-job SMS toggle) had no equivalent.
 * "Process Device Data" is this migration's own pre-existing convenience,
 * not part of the real `student-attendance-input` page itself (the old app's
 * device-log flow is its own separate page/url) — left as is.
 */
export default function StudentAttendanceTakePage() {
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [date, setDate] = useState(today());
  const [sendSms, setSendSms] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([listClassConfigurations(), periodApi.list(), academicYearApi.list()]).then(
      ([configs, periodList, years]) => {
        setClassConfigurations(configs);
        setPeriods(periodList);
        setAcademicYears(years);
        if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
        if (periodList.length > 0) setPeriodId(String(periodList[0].id));
        if (years.length > 0) setAcademicYear(years[0].name);
      }
    );
  }, []);

  const loadGrid = () => {
    if (!classConfigurationId || !periodId || !date || !academicYear) return Promise.resolve();
    setLoading(true);
    setError("");
    return getStudentAttendanceGrid(classConfigurationId, periodId, date, academicYear)
      .then(setRows)
      .catch(() => setError("Failed to load attendance grid."))
      .finally(() => setLoading(false));
  };

  // Adding `academicYear` as a 4th independent filter (this page's own fix — see the
  // top doc comment) means changing class/year/period/date in quick succession can now
  // fire several overlapping requests; without a guard, a slower-but-earlier request
  // can resolve after a faster-but-later one and overwrite the grid with stale data
  // (caught live: switching class then year in the same batch briefly showed the
  // *previous* class+year's roster). `cancelled` ignores any response that resolves
  // after this effect has already been superseded by a newer one.
  useEffect(() => {
    let cancelled = false;
    if (!classConfigurationId || !periodId || !date || !academicYear) return undefined;
    setLoading(true);
    setError("");
    getStudentAttendanceGrid(classConfigurationId, periodId, date, academicYear)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load attendance grid.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [classConfigurationId, periodId, date, academicYear]);

  const setStatus = (identificationId, status) => {
    setRows((prev) => prev.map((r) => (r.identificationId === identificationId ? { ...r, attendanceStatus: status } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const entries = rows.map((r) => ({
        identificationId: r.identificationId,
        attendanceStatus: r.attendanceStatus || 2,
        attendanceComment: r.attendanceComment,
      }));
      const updated = await saveStudentAttendance({
        classConfigurationId: Number(classConfigurationId),
        periodId: Number(periodId),
        date,
        academicYear,
        sendSms,
        entries,
      });
      setRows(updated);
      setSuccess("Attendance saved.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  const handleProcessDevice = async () => {
    setProcessing(true);
    setError("");
    setSuccess("");
    try {
      const result = await processStudentDeviceLog({ classConfigurationId: Number(classConfigurationId), periodId: Number(periodId), date });
      setSuccess(result.message);
      loadGrid();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to process device log.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Student Attendance</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigurations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary" onClick={handleProcessDevice} disabled={processing}>
          {processing ? "Processing..." : "Process Device Data"}
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Roll</th>
                <th>Student</th>
                <th>Punch In</th>
                <th>Status</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.identificationId}>
                  <td>{row.studentRoll}</td>
                  <td>{row.studentName}</td>
                  <td>{row.punchInTime || "—"}</td>
                  <td>
                    <select value={row.attendanceStatus} onChange={(e) => setStatus(row.identificationId, Number(e.target.value))}>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{row.attendanceComment || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="form-actions" style={{ justifyContent: "flex-end", alignItems: "center" }}>
            <label style={{ marginRight: 12 }}>
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} /> Send Sms
            </label>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Attendance"}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
